import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../config/apiBase";
import { getAuthUserHeader, getUserRole, readStoredAuthUser } from "../utils/userRoles";

const PREPARATION_INTERVAL_MS = 2500;

function normaliseId(value) {
  return String(value || "").trim().toUpperCase();
}

export default function MentorPreparationPage() {
  const { studentId: routeStudentId, weekId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const week = Number(weekId);
  const authUser = readStoredAuthUser() || {};
  const authenticatedStudentId = normaliseId(
    authUser.student_id || authUser.username || localStorage.getItem("student_id"),
  );
  const requestedPreview = new URLSearchParams(location.search).get("preview") === "admin";
  const role = getUserRole(authUser, localStorage.getItem("role"));
  const adminPreview = requestedPreview && (role === "admin" || role === "tutor");
  const studentId = adminPreview ? normaliseId(routeStudentId) : authenticatedStudentId;
  const validWeek = Number.isInteger(week) && week >= 1 && week <= 4;
  const canRequest = Boolean(studentId && validWeek);

  const [status, setStatus] = React.useState("pending");
  const [error, setError] = React.useState("");
  const mountedRef = React.useRef(false);
  const requestRef = React.useRef(0);
  const timerRef = React.useRef(null);
  const controllerRef = React.useRef(null);
  const statusRef = React.useRef("pending");

  const stopPolling = React.useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const applyStatus = React.useCallback((value, requestId) => {
    if (!mountedRef.current || requestId !== requestRef.current) return;
    if (statusRef.current === "ready_unacknowledged" && value !== "acknowledged") return;
    const next = value === "ready" ? "ready_unacknowledged" : value || "not_ready";
    statusRef.current = next;
    setStatus(next);
    if (next === "ready_unacknowledged" || next === "acknowledged") {
      setError("");
      stopPolling();
    }
  }, [stopPolling]);

  const check = React.useCallback(async () => {
    if (!studentId || !validWeek || !mountedRef.current) return;
    const requestId = ++requestRef.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/weeks/${week}/status?student_id=${encodeURIComponent(studentId)}`,
        { headers: { "X-User": getAuthUserHeader() }, signal: controller.signal },
      );
      const data = await response.json();
      if (!response.ok) throw new Error("status_failed");
      if (data.status === "disabled") {
        if (data.next_url && mountedRef.current) navigate(data.next_url);
        return;
      }
      applyStatus(data.status || data.mentor_status, requestId);
    } catch (requestError) {
      // Abort is an expected lifecycle event (unmount, Strict Mode replay, or
      // replacement by a newer poll), never a user-facing preparation error.
      if (controller.signal.aborted || requestError?.name === "AbortError" || !mountedRef.current || requestId !== requestRef.current) return;
      setError("We could not prepare your Mentor review yet. Please try again.");
      if (statusRef.current !== "ready_unacknowledged") {
        statusRef.current = "failed";
        setStatus("failed");
      }
    }
  }, [applyStatus, navigate, studentId, validWeek, week]);

  React.useEffect(() => {
    mountedRef.current = true;
    if (!studentId || !validWeek) return () => { mountedRef.current = false; };
    check();
    timerRef.current = setInterval(check, PREPARATION_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
      controllerRef.current?.abort();
      stopPolling();
    };
  }, [check, studentId, stopPolling, validWeek]);

  const retry = async () => {
    if (!canRequest || !mountedRef.current) return;
    requestRef.current += 1;
    const requestId = requestRef.current;
    setError(""); statusRef.current = "pending"; setStatus("pending");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/weeks/${week}/prepare?student_id=${encodeURIComponent(studentId)}`,
        { method: "POST", headers: { "X-User": getAuthUserHeader() } },
      );
      const data = await response.json();
      if (!response.ok) throw new Error("prepare_failed");
      applyStatus(data.status || data.mentor_status, requestId);
      if (data.status !== "ready" && data.status !== "ready_unacknowledged") check();
    } catch {
      if (mountedRef.current && requestId === requestRef.current) {
        setError("We could not prepare your Mentor review yet. Please try again.");
        statusRef.current = "failed"; setStatus("failed");
      }
    }
  };

  if (status === "ready_unacknowledged") return <main className="mentor-page"><h1>Your Mentor Report Is Ready</h1><p>Your Week {week} Mentor review is ready to read.</p><button type="button" onClick={() => navigate(`/student/${studentId}/week-mentor/${week}`)}>Show Mentor Report</button></main>;
  if (status === "acknowledged") return <main className="mentor-page"><h1>Your Mentor Report Is Ready</h1><p>This review has already been acknowledged.</p><button type="button" onClick={() => navigate(week === 4 ? `/student/${studentId}/course-complete` : `/student/${studentId}/week/${week + 1}/start`)}>Continue</button></main>;
  if (error || status === "failed") return <main className="mentor-page"><p>{error || "We could not prepare your Mentor review yet. Please try again."}</p><button type="button" onClick={retry}>Try Again</button></main>;
  return <main className="mentor-page"><h1>Preparing Your Mentor Review</h1><p>Please wait — your Mentor report is being prepared.</p><p role="status" aria-label="Preparing">Loading…</p></main>;
}
